/**
 * Canonical default CRM partner buy-rate grids.
 *
 * SINGLE SOURCE OF TRUTH for the go-forward wholesale CRM buy rate Tagada
 * signs with partners. Previously this grid was copy-pasted across the
 * contract scripts (`scripts/send-partner-*`) and the CRM operator UI
 * (`apps/crm-app/.../billing/page.tsx`), which let the numbers drift. Every
 * consumer must now import from here.
 *
 * This is the BUY RATE (the floor Tagada keeps), NOT the merchant sell rate.
 * The partner stacks their own markup on top; the merchant only ever sees
 * buy + markup.
 *
 * Shape mirrors `src/lib/partners/buyRateSchemas.ts` (`crmBuyRateGridSchema`)
 * one-to-one. Callers in `src/`/scripts may still run the constant through
 * `crmBuyRateGridSchema.parse(...)` for runtime validation — it is a plain
 * object literal that satisfies the schema.
 *
 * Lives in `@tagadapay/billing-core` because that is the only package
 * importable by `src/`, the contract scripts AND `apps/crm-app` (the SPA
 * cannot reach `src/lib/...`).
 */
import type { CrmBuyRateGrid } from '../compute/sell-rate';

/**
 * Go-forward standard partner CRM buy rate (Jun 2026):
 *   - weekly platform fee: $49/week (buy rate the partner pays Tagada),
 *     charged on any week with ≥1 approved txn (minApprovedTxns=1, the
 *     canonical "charge if active" gate; null would mean never charge)
 *   - approved transactions: 0.80% volume, no fixed component
 *   - upsell / subscription: +1.00% each (volume surcharge on top of approved)
 *   - declined / refund: $0; automatic retry: 0.20% on successful retry only
 *   - 3DS verification: $0.07
 *   - applePay / googlePay omitted — wallet txns bill at the approved rate
 *   - applePayDomain (Tagada standalone): $12.50/domain/week
 *   - customDomain: $2.00/domain/week
 */
export const STANDARD_CRM_BUY_RATE_GRID: CrmBuyRateGrid = {
  weeklyBase: { enabled: true, amountCents: 4900, minApprovedTxns: 1 },
  approvedTransaction: { enabled: true, fixedCents: 0, bps: 80 },
  declinedTransaction: { enabled: true, fixedCents: 0, bps: 0 },
  refund: { enabled: true, fixedCents: 0, bps: 0 },
  threeDsVerification: { enabled: true, fixedCents: 7, bps: 0 },
  subscription: { enabled: true, bps: 100 },
  upsell: { enabled: true, bps: 100 },
  automaticRetry: { enabled: true, bps: 20 },
  applePayDomain: { enabled: true, fixedCents: 1250 },
  customDomain: { enabled: true, fixedCents: 200 },
};

/**
 * VeraSolution v3 — identical to the standard grid except:
 *   - approved transactions bill a flat $0.25/txn instead of 0.80% volume;
 *   - NO weekly platform fee (negotiated out — kept explicit so the $49
 *     weekly base now on the standard grid does NOT leak in via the spread).
 */
export const VERASOLUTION_CRM_BUY_RATE_GRID: CrmBuyRateGrid = {
  ...STANDARD_CRM_BUY_RATE_GRID,
  weeklyBase: { enabled: false, amountCents: 0, minApprovedTxns: null },
  approvedTransaction: { enabled: true, fixedCents: 25, bps: 0 },
};

/**
 * Deep clone of a buy-rate grid. Use this whenever a consumer is going to
 * MUTATE the grid (e.g. an editable operator form) so edits never leak into
 * the shared module-level preset.
 */
export function cloneCrmBuyRateGrid(grid: CrmBuyRateGrid): CrmBuyRateGrid {
  return JSON.parse(JSON.stringify(grid)) as CrmBuyRateGrid;
}
